import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { REPEATER_BAND } from '../theme/theme';
import { useFormReadOnly } from './formReadOnly';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// ---------------------------------------------------------------------------
/**
 * The repeater every "a record may name several of these" list is built from:
 * the day's Sondagen, a Planum's Farben, Beimengungen and Schichten, a
 * Tagebuch's Besuch. Only the FIELDS of a row differ, so that is all a caller
 * writes — `renderRow` returns them and gets back an `update` that patches
 * just that row.
 *
 * A row that is itself several fields tall can ask for `band`: it then sits on
 * a tinted ground running to the EDGES of the surrounding section box.
 */
// ---------------------------------------------------------------------------

/**
 * A local `id`, React key and nothing else — never persisted. Keying by array
 * index instead moves the focus when a row above is removed, and re-keys every
 * field of every row below it.
 */
export interface RepeaterRow {
    id: string;
}

export default function RepeaterList<T extends RepeaterRow>({
    rows,
    onChange,
    blankRow,
    renderRow,
    labelId,
    emptyId,
    addId,
    removeId = 'common.delete',
    spacing = 1.5,
    align = 'flex-start',
    wrap = false,
    band = false,
    removeAt = 'side',
    renderHeader,
    max
}: {
    rows: T[];
    onChange: (rows: T[]) => void;
    /** The row an "add" makes — this component gives it its id. */
    blankRow: Omit<T, 'id'>;
    renderRow: (row: T, update: (patch: Partial<T>) => void) => ReactNode;
    /** Heading above the list — omit where the surrounding section titles it. */
    labelId?: string;
    emptyId: string;
    addId: string;
    /** The delete button's label, where "Löschen" is not specific enough. */
    removeId?: string;
    /**
     * Between rows. Roomier (3) where one row is several fields tall. Ignored
     * under `band`, where the gap is the paper showing between two tinted
     * grounds and has to stay narrow enough to read as one list.
     */
    spacing?: number;
    /** How the delete button lines up with a row of unequal-height fields. */
    align?: 'flex-start' | 'center';
    /** Let a row's fields wrap onto a second line (long rows on a phone). */
    wrap?: boolean;
    /**
     * Put each row on its own tinted ground, with a header line above its
     * fields — for a row that is a BLOCK rather than a line (the Lagen of a
     * baulicher Bestand). Unlike a card, the ground runs EDGE TO EDGE: it
     * overhangs the section box's padding and insets its own content by the
     * same amount, so the fields keep the one left edge the rest of the form
     * stands on.
     *
     * The overhang is FormSection's padding (`{ xs: 2, sm: 3 }`). A repeater
     * mounted outside such a box must leave this off, or the tint bleeds into
     * whatever surrounds it.
     */
    band?: boolean;
    /**
     * `side` — at the end of the row, which suits a row whose fields ARE the
     * row (a Besuch, a Farbe). `header` puts it in the band's header line
     * instead. Only a `band` row can choose; a plain row has no header.
     */
    removeAt?: 'side' | 'header';
    /** The band's header line, left of the delete button. `band` rows only. */
    renderHeader?: (row: T, update: (patch: Partial<T>) => void) => ReactNode;
    /** At most this many rows: the add button goes once they are there. */
    max?: number;
}) {
    const intl = useIntl();
    const theme = useTheme();
    // A reader sees the rows, not the means to add or remove them.
    const readOnly = useFormReadOnly();

    function update(id: string, patch: Partial<T>) {
        onChange(
            rows.map((row) => (row.id === id ? { ...row, ...patch } : row))
        );
    }

    return (
        <Box>
            {labelId && (
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 0.5,
                        fontWeight: 'bold',
                        color: 'text.secondary'
                    }}
                >
                    <FormattedMessage id={labelId} />
                </Typography>
            )}
            {/* `useFlexGap` is what makes the band possible: Stack spaces by
                MARGIN and resets `margin: 0` on every direct child through
                `.MuiStack-root > :not(style):not(style)`, which outspecifies
                the child's `sx` — the band's negative side margins would be
                dropped and the tint would stop at the padding. */}
            <Stack spacing={band ? 1 : spacing} useFlexGap={band}>
                {rows.map((row) => {
                    const patchRow = (patch: Partial<T>) =>
                        update(row.id, patch);
                    const removeLabel = intl.formatMessage({ id: removeId });
                    const removeButton = readOnly ? null : (
                        <Tooltip title={removeLabel}>
                            <IconButton
                                aria-label={removeLabel}
                                color="error"
                                onClick={() =>
                                    onChange(
                                        rows.filter((it) => it.id !== row.id)
                                    )
                                }
                                sx={{ alignSelf: 'center' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    );
                    const fieldRow = (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: align,
                                flexWrap: wrap ? 'wrap' : 'nowrap'
                            }}
                        >
                            {renderRow(row, patchRow)}
                            {removeAt === 'side' && removeButton}
                        </Box>
                    );
                    if (band) {
                        const header =
                            renderHeader || removeAt === 'header' ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: 0.5,
                                        mb: 1.5
                                    }}
                                >
                                    {renderHeader?.(row, patchRow)}
                                    {removeAt === 'header' && removeButton}
                                </Box>
                            ) : null;
                        return (
                            <Box
                                key={row.id}
                                sx={{
                                    /**
                                     * See REPEATER_BAND for why light and dark
                                     * cannot lift by the same step.
                                     */
                                    bgcolor: REPEATER_BAND[theme.palette.mode],
                                    // Out past the section box's padding, then
                                    // back in by the same amount — see `band`.
                                    mx: { xs: -2, sm: -3 },
                                    px: { xs: 2, sm: 3 },
                                    /** A header carries its own top margin. */
                                    pt: header ? 1 : 1.5,
                                    pb: 1.5
                                }}
                            >
                                {header}
                                {removeAt === 'side'
                                    ? fieldRow
                                    : renderRow(row, patchRow)}
                            </Box>
                        );
                    }
                    return <Box key={row.id}>{fieldRow}</Box>;
                })}
            </Stack>
            {rows.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage id={emptyId} />
                </Typography>
            )}
            {!readOnly && (max === undefined || rows.length < max) && (
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() =>
                        onChange([...rows, { ...blankRow, id: uuid() } as T])
                    }
                    sx={{ mt: 1.25, alignSelf: 'flex-start' }}
                >
                    <FormattedMessage id={addId} />
                </Button>
            )}
        </Box>
    );
}
